import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  MdVpnKey,
  MdCheckCircle,
  MdOutlineContentCopy,
  MdWarning,
  MdAdd,
  MdDeleteOutline,
  MdRefresh,
  MdHelpOutline,
  MdClose
} from "react-icons/md";
import {
  getMyCompanySSO,
  saveSSOConnection,
  addSSODomain,
  verifySSODomain,
  deleteSSODomain,
  toggleEnforceSSO,
  initiateSSOLogin,
  SSOConnectionData,
  SSODomainItem
} from "../../../utils/api/endpoints/sso";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";

const SSOSettings: React.FC = () => {
  const { email } = useUserCredentials();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [verifyingId, setVerifyingId] = useState<number | null>(null);

  const [connection, setConnection] = useState<SSOConnectionData | null>(null);
  const [enforceSSO, setEnforceSSO] = useState<boolean>(false);

  // Form states
  const [provider, setProvider] = useState<string>("okta");
  const [displayName, setDisplayName] = useState<string>("");
  const [oidcIssuer, setOidcIssuer] = useState<string>("");
  const [oidcClientId, setOidcClientId] = useState<string>("");
  const [oidcClientSecret, setOidcClientSecret] = useState<string>("");
  const [status, setStatus] = useState<"draft" | "testing" | "active" | "disabled">("draft");

  // Domain addition state
  const [newDomain, setNewDomain] = useState<string>("");
  const [addingDomain, setAddingDomain] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showActiveWarningModal, setShowActiveWarningModal] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const data = await getMyCompanySSO();
    if (data) {
      setEnforceSSO(data.enforce_sso);
      if (data.connection) {
        setConnection(data.connection);
        setProvider(data.connection.provider || "okta");
        setDisplayName(data.connection.display_name || "");
        setOidcIssuer(data.connection.oidc_issuer || "");
        setOidcClientId(data.connection.oidc_client_id || "");
        setOidcClientSecret(data.connection.oidc_client_secret || "");
        setStatus(data.connection.status || "draft");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "SSO_ERROR" && event.data?.message) {
        toast.error(event.data.message);
      } else if (event.data?.type === "SSO_TEST_SUCCESS") {
        toast.success(
          event.data?.message || "SSO Test Login Verified! Status updated to Testing."
        );
        loadData();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [loadData]);

  const handleSaveConnection = async (e: React.FormEvent, skipWarning = false) => {
    e.preventDefault();

    const isEditingActiveCredentials =
      connection?.status === "active" &&
      (oidcIssuer.trim() !== (connection?.oidc_issuer || "") ||
        oidcClientId.trim() !== (connection?.oidc_client_id || "") ||
        (oidcClientSecret.trim() !== "" && oidcClientSecret.trim() !== "••••••••"));

    if (isEditingActiveCredentials && !skipWarning) {
      setShowActiveWarningModal(true);
      return;
    }

    setShowActiveWarningModal(false);
    setSaving(true);
    const result = await saveSSOConnection({
      provider,
      display_name: displayName || "Company Okta SSO",
      oidc_issuer: oidcIssuer,
      oidc_client_id: oidcClientId,
      oidc_client_secret: oidcClientSecret,
      status
    });
    setSaving(false);
    if (result) {
      loadData();
    }
  };

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain) return;
    setAddingDomain(true);
    const res = await addSSODomain(newDomain);
    setAddingDomain(false);
    if (res) {
      setNewDomain("");
      loadData();
    }
  };

  const handleVerifyDomain = async (domainId: number) => {
    setVerifyingId(domainId);
    await verifySSODomain(domainId);
    setVerifyingId(null);
    loadData();
  };

  const handleDeleteDomain = async (domainId: number) => {
    if (window.confirm("Are you sure you want to remove this domain?")) {
      const success = await deleteSSODomain(domainId);
      if (success) loadData();
    }
  };

  const handleToggleEnforce = async () => {
    const nextVal = !enforceSSO;
    const res = await toggleEnforceSSO(nextVal);
    if (res !== null) {
      setEnforceSSO(res);
    }
  };

  const handleTestConnection = async () => {
    if (isFormDirty) {
      toast.error("Please click 'Save Connection Details' before testing!");
      return;
    }

    const verifiedDomain = connection?.Domains?.find(d => d.is_verified);
    if (!verifiedDomain && (!connection?.Domains || connection.Domains.length === 0)) {
      toast.error("Please register and verify your company domain in Section 2 below before testing!");
      return;
    }

    const emailToTest = email || `admin@${verifiedDomain?.domain || "company.com"}`;
    setTestingConnection(true);
    try {
      const res = await initiateSSOLogin(emailToTest, true);
      if (res?.redirectUrl) {
        toast.success("Initiating Okta test login window...");
        window.open(res.redirectUrl, "_blank", "width=600,height=700");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Test connection failed.");
    } finally {
      setTestingConnection(false);
    }
  };

  const sanitizeUrl = (urlStr: string): string => {
    if (!urlStr) return "";
    return urlStr.trim().replace(/([^:]\/)\/+/g, "$1");
  };

  const rawBackendUrl = (
    process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:3001"
  ).trim();

  let baseOrigin = rawBackendUrl
    .replace(/\/+api\/?$/i, "")
    .replace(/\/+$/, "");

  if (!baseOrigin.startsWith("http://") && !baseOrigin.startsWith("https://")) {
    const origin = window.location.origin.replace(/\/+$/, "");
    const cleanPath = baseOrigin
      ? baseOrigin.startsWith("/")
        ? baseOrigin
        : `/${baseOrigin}`
      : "";
    baseOrigin = `${origin}${cleanPath}`.replace(/\/+$/, "");
  }

  const callbackUrl = sanitizeUrl(`${baseOrigin}/api/v2/sso/callback`);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const isFormDirty = useMemo(() => {
    if (!connection) {
      return Boolean(
        oidcIssuer.trim() || oidcClientId.trim() || oidcClientSecret.trim()
      );
    }
    return (
      provider !== (connection.provider || "okta") ||
      displayName !== (connection.display_name || "") ||
      oidcIssuer.trim() !== (connection.oidc_issuer || "") ||
      oidcClientId.trim() !== (connection.oidc_client_id || "") ||
      (oidcClientSecret.trim() !== "" && oidcClientSecret.trim() !== "••••••••") ||
      status !== (connection.status || "draft")
    );
  }, [
    provider,
    displayName,
    oidcIssuer,
    oidcClientId,
    oidcClientSecret,
    status,
    connection
  ]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved SSO settings changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty]);

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        Loading Single Sign-On settings...
      </div>
    );
  }

  const hasVerifiedDomain = connection?.Domains?.some(d => d.is_verified) ?? false;
  const hasTestedConnection = Boolean(
    connection && ["testing", "active"].includes(connection.status) && !isFormDirty
  );

  const canEnforceSSO = status === "active" && hasVerifiedDomain && !isFormDirty;

  const enforceDisabledTooltip = !canEnforceSSO
    ? isFormDirty
      ? "Please click 'Save Connection Details' above before toggling enforcement."
      : status !== "active"
      ? "Connection Status must be set to 'Active (Live)' to enforce SSO for team members."
      : "At least one email domain in Section 2 must be verified to enforce SSO for team members."
    : "";

  return (
    <div className="space-y-8 w-full font-sans">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-[#010D2C]/5 rounded-lg text-[#010D2C]">
            <MdVpnKey className="w-6 h-6" />
          </div>
          <div>
            <h2
              className="text-xl text-gray-900"
              style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600 }}
            >
              Single Sign-On (SSO)
            </h2>
            <p className="text-sm text-gray-500">
              Configure Okta or OpenID Connect (OIDC) so your team can log in with company credentials.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowGuideModal(true)}
          className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-[#010D2C] bg-white border border-gray-300 rounded-lg shadow-2xs hover:bg-gray-50 transition-colors"
          style={{ fontFamily: "Montserrat, sans-serif" }}
        >
          <MdHelpOutline className="w-4 h-4 text-[#58CC02]" />
          View Okta Setup Guide
        </button>
      </div>

      {/* Setup Guide Modal */}
      {showGuideModal &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
            <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 p-6 max-h-[90vh] overflow-y-auto space-y-6">
              <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-[#010D2C] text-white rounded-md">
                    <MdVpnKey className="w-5 h-5" />
                  </div>
                  <div>
                    <h3
                      className="text-base font-semibold text-gray-900"
                      style={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      Okta SSO Integration & Setup Guide
                    </h3>
                    <p className="text-xs text-gray-500">
                      Follow these 4 simple steps to connect your company's Okta directory to CTB.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowGuideModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-700 rounded-md transition-colors"
                >
                  <MdClose className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-5 text-xs text-gray-700 leading-relaxed">
                {/* Step 1 */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                  <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#010D2C] text-white text-[10px]">
                      1
                    </span>
                    Create App Integration in Okta Admin Console
                  </h4>
                  <p>
                    Log in to your{" "}
                    <a
                      href="https://login.okta.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline font-semibold hover:text-blue-800"
                    >
                      Okta Admin Console
                    </a>{" "}
                    → go to <strong>Applications → Applications</strong> → click <strong>Create App Integration</strong>.
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li><strong>Sign-in method:</strong> OIDC - OpenID Connect</li>
                    <li><strong>Application type:</strong> Web Application</li>
                    <li><strong>Controlled access (Assignments):</strong> Select <em>Allow everyone in your organization to access</em> (or restrict to selected groups).</li>
                  </ul>
                </div>

                {/* Step 2 */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                  <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#010D2C] text-white text-[10px]">
                      2
                    </span>
                    Set Sign-in Redirect URI
                  </h4>
                  <p>
                    Under Okta Application Settings, paste the following <strong>Sign-in Redirect URI</strong>:
                  </p>
                  <div className="flex items-center justify-between p-2.5 bg-slate-900 text-white rounded-md font-mono text-[11px]">
                    <span>{callbackUrl}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(callbackUrl);
                        toast.success("Redirect URI copied to clipboard!");
                      }}
                      className="flex items-center gap-1 px-2 py-1 bg-white/10 hover:bg-white/20 rounded text-xs transition-colors"
                    >
                      <MdOutlineContentCopy className="w-3.5 h-3.5" />
                      Copy
                    </button>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                  <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#010D2C] text-white text-[10px]">
                      3
                    </span>
                    Copy Credentials to CTB & Save Details
                  </h4>
                  <p>
                    Copy your <strong>Okta Issuer URL</strong> (e.g. <code>https://your-company.okta.com</code>), <strong>Client ID</strong>, and <strong>Client Secret</strong> from Okta's <em>General</em> tab into Section 1 on this page and click <strong>Save Connection Details</strong>.
                  </p>
                  <div className="p-2.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-md text-[11px] font-medium space-y-1">
                    <p>
                      ⚠️ <strong>CRITICAL OKTA URL NOTE:</strong> Remove <code>-admin</code> from your Okta Domain!
                    </p>
                    <p className="text-amber-800">
                      If your browser URL says <code>https://yourcompany-admin.okta.com</code>, strip out <code>-admin</code> so it reads <code>https://yourcompany.okta.com</code>. Entering <code>-admin</code> will cause OIDC discovery to fail.
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                  <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#010D2C] text-white text-[10px]">
                      4
                    </span>
                    Verify Domain, Run Test Connection & Go Live
                  </h4>
                  <p>
                    Add your corporate domain (e.g. <code>acme.com</code>) under Section 2 and click <strong>Verify DNS Record</strong>.
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600">
                    <li>Click <strong>Test SSO Connection</strong> to perform a test login and verify your Client ID & Secret.</li>
                    <li>Upon successful test login, the status automatically advances from <strong>Draft</strong> ➔ <strong>Testing</strong>.</li>
                    <li>When ready to go live for all employees, change Connection Status to <strong>Active (Live)</strong> and optionally toggle <strong>Enforce SSO</strong> ON.</li>
                  </ul>
                  <div className="p-2.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md text-[11px] font-medium">
                    💡 <strong>Note:</strong> Once your domain displays the green <em>Verified</em> badge, you can safely delete the <code>_ctb-verify</code> TXT record from your DNS panel.
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowGuideModal(false)}
                  className="px-5 py-2 text-xs font-semibold text-white bg-[#010D2C] hover:bg-[#081b4d] rounded-lg transition-colors"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Got It, Close Guide
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {isFormDirty && (
        <div className="p-3.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg text-xs flex items-center justify-between font-medium shadow-2xs">
          <span className="flex items-center gap-2">
            <MdWarning className="w-4 h-4 text-amber-600 flex-shrink-0" />
            You have unsaved SSO configuration changes. Click "Save Connection Details" below to apply them.
          </span>
        </div>
      )}

      {/* 1. Okta / OIDC Connection Details */}
      <form
        onSubmit={handleSaveConnection}
        className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-6"
      >
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
          <h3
            className="text-sm font-semibold uppercase tracking-wider text-gray-700"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            1. Identity Provider Configuration
          </h3>
          <span
            className={`px-3 py-1 text-xs font-semibold rounded-full uppercase tracking-wider ${
              status === "active"
                ? "bg-[#E8F8D8] text-[#2E7D00]"
                : status === "testing"
                ? "bg-amber-100 text-amber-800"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            {status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Provider
            </label>
            <select
              value={provider}
              onChange={e => setProvider(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-[#010D2C] focus:outline-none"
            >
              <option value="okta">Okta (OIDC)</option>
              <option value="custom_oidc">Generic OpenID Connect (OIDC)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Connection Display Name
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Acme Corp Okta"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-[#010D2C] focus:outline-none"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Okta Issuer URL
            </label>
            <input
              type="url"
              required
              placeholder="https://integrator-3996449.okta.com"
              value={oidcIssuer}
              onChange={e => setOidcIssuer(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-[#010D2C] focus:outline-none"
            />
            <p className="mt-1 text-xs text-gray-500">
              Found in your Okta Admin Console under Applications &gt; Your App &gt; Issuer (without -admin).
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Client ID
            </label>
            <input
              type="text"
              required
              placeholder="0oa..."
              value={oidcClientId}
              onChange={e => setOidcClientId(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-[#010D2C] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Client Secret
            </label>
            <input
              type="password"
              placeholder="Client Secret"
              value={oidcClientSecret}
              onChange={e => setOidcClientSecret(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-[#010D2C] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Connection Status
            </label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as any)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-[#010D2C] focus:outline-none"
            >
              <option value="draft">Draft (Inactive)</option>
              <option value="testing">Testing</option>
              <option value="active" disabled={!hasTestedConnection}>
                Active (Live){!hasTestedConnection ? " - Requires successful 'Test SSO Connection'" : ""}
              </option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>

        {/* Redirect URI snippet */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Okta Application Sign-in Redirect URI
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={callbackUrl}
              className="flex-1 bg-white border border-gray-300 rounded px-3 py-1.5 text-xs text-gray-800 font-mono"
            />
            <button
              type="button"
              onClick={() => copyToClipboard(callbackUrl, "Redirect URI")}
              className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 rounded text-xs font-medium text-gray-800 flex items-center gap-1 transition-colors"
            >
              <MdOutlineContentCopy /> Copy
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Paste this URI into your Okta Web Application configuration under "Sign-in redirect URIs".
          </p>
        </div>

        <div className="flex justify-end items-center gap-3 pt-2">
          {oidcIssuer && (
            <div className="relative group inline-block">
              <button
                type="button"
                disabled={!hasVerifiedDomain || isFormDirty || testingConnection}
                onClick={handleTestConnection}
                className={`px-4 py-2.5 text-sm font-semibold rounded-lg border transition-colors flex items-center gap-2 ${
                  !hasVerifiedDomain || isFormDirty
                    ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    : "bg-blue-50 hover:bg-blue-100 text-blue-900 border-blue-200 cursor-pointer"
                }`}
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                <MdVpnKey
                  className={`w-4 h-4 ${
                    !hasVerifiedDomain || isFormDirty ? "text-gray-400" : "text-blue-700"
                  }`}
                />
                {testingConnection
                  ? "Launching Test..."
                  : hasTestedConnection
                  ? "Re-test SSO Connection"
                  : "Test SSO Connection"}
              </button>

              {(!hasVerifiedDomain || isFormDirty) && (
                <div className="absolute bottom-full right-0 mb-2.5 hidden group-hover:flex flex-col items-center z-50 pointer-events-none w-64">
                  <div className="bg-slate-900 text-white text-[11px] font-medium px-3 py-2 rounded-lg shadow-xl text-center leading-normal border border-slate-700 w-full whitespace-normal">
                    {isFormDirty
                      ? "Please click 'Save Connection Details' before testing"
                      : "Please register & verify your domain in Section 2 below before testing"}
                  </div>
                  <div className="w-2.5 h-2.5 bg-slate-900 rotate-45 -mt-1.5 border-r border-b border-slate-700"></div>
                </div>
              )}
            </div>
          )}
          <button
            type="submit"
            disabled={saving || !isFormDirty}
            className={`px-5 py-2.5 text-sm font-semibold rounded-lg shadow transition-colors ${
              !isFormDirty || saving
                ? "bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300"
                : "bg-[#010D2C] hover:bg-[#081845] text-white cursor-pointer"
            }`}
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            {saving ? "Saving Configuration..." : "Save Connection Details"}
          </button>
        </div>
      </form>

      {/* 2. Domain Verification Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-6">
        <div className="border-b border-gray-100 pb-4">
          <h3
            className="text-sm font-semibold uppercase tracking-wider text-gray-700"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            2. Email Domain Ownership & Verification
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Register your company's email domain (e.g. acme.com) and publish a DNS TXT record to prove domain ownership before SSO routing is activated.
          </p>
        </div>

        {/* Add Domain Form */}
        <form onSubmit={handleAddDomain} className="flex gap-3">
          <input
            type="text"
            placeholder="acme.com"
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-[#010D2C] focus:outline-none"
          />
          <button
            type="submit"
            disabled={addingDomain || !newDomain}
            className="px-4 py-2 bg-[#010D2C] hover:bg-[#081845] text-white text-sm font-medium rounded-md flex items-center gap-1 disabled:opacity-50"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            <MdAdd className="w-5 h-5" /> Add Domain
          </button>
        </form>

        {/* Registered Domains List */}
        <div className="space-y-4">
          {!connection?.Domains || connection.Domains.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              No domains registered yet. Add your company domain above.
            </p>
          ) : (
            connection.Domains.map((d: SSODomainItem) => (
              <div
                key={d.id}
                className="p-4 rounded-lg border border-gray-200 bg-gray-50 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-gray-900">
                      {d.domain}
                    </span>
                    {d.is_verified ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#E8F8D8] text-[#2E7D00]">
                        <MdCheckCircle className="w-4 h-4" /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                        Pending DNS Verification (Expires in 72h)
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteDomain(d.id)}
                    className="p-1 text-red-500 hover:text-red-700 transition-colors"
                    title="Remove domain"
                  >
                    <MdDeleteOutline className="w-5 h-5" />
                  </button>
                </div>

                {!d.is_verified && (
                  <div className="p-3 bg-white rounded border border-amber-200 space-y-2">
                    <p className="text-xs font-medium text-gray-700">
                      Add the following TXT record to your domain's DNS settings:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono">
                      <div>
                        <span className="text-gray-500">Host:</span>{" "}
                        <span className="text-gray-900 select-all">
                          _ctb-verify.{d.domain}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-gray-500">Value:</span>{" "}
                          <span className="text-gray-900 select-all">
                            {d.dns_txt_record}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(d.dns_txt_record, "DNS TXT Record")}
                          className="text-blue-600 hover:underline flex items-center gap-1 ml-2"
                        >
                          <MdOutlineContentCopy /> Copy
                        </button>
                      </div>
                    </div>

                    <div className="pt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleVerifyDomain(d.id)}
                        disabled={verifyingId === d.id}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded flex items-center gap-1 disabled:opacity-50"
                      >
                        <MdRefresh className={`w-4 h-4 ${verifyingId === d.id ? "animate-spin" : ""}`} />
                        {verifyingId === d.id ? "Verifying DNS..." : "Verify DNS Record"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* 3. Enforcement Toggle Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3
              className="text-sm font-semibold uppercase tracking-wider text-gray-700"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              3. Enforce SSO for Team Members
            </h3>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">
              When enforced, all team members in your organization must log in via Single Sign-On. Password login will be disabled for team members. As company owner, you will retain password access for account recovery.
            </p>
          </div>
          <div className="relative group inline-block">
            <button
              type="button"
              disabled={!canEnforceSSO && !enforceSSO}
              onClick={handleToggleEnforce}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                !canEnforceSSO && !enforceSSO
                  ? "bg-gray-200 cursor-not-allowed"
                  : enforceSSO
                  ? "bg-[#58CC02] cursor-pointer"
                  : "bg-gray-300 cursor-pointer"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  enforceSSO ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>

            {!canEnforceSSO && !enforceSSO && (
              <div className="absolute bottom-full right-0 mb-2.5 hidden group-hover:flex flex-col items-center z-50 pointer-events-none w-64">
                <div className="bg-slate-900 text-white text-[11px] font-medium px-3 py-2 rounded-lg shadow-xl text-center leading-normal border border-slate-700 w-full whitespace-normal">
                  {enforceDisabledTooltip}
                </div>
                <div className="w-2.5 h-2.5 bg-slate-900 rotate-45 -mt-1.5 border-r border-b border-slate-700"></div>
              </div>
            )}
          </div>
        </div>

        {enforceSSO && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3 text-xs text-amber-800">
            <MdWarning className="w-5 h-5 flex-shrink-0 text-amber-600" />
            <span>
              SSO Enforcement is active. Team members attempting password login will be directed to use Single Sign-On.
            </span>
          </div>
        )}
      </div>

      {/* Active Connection Modification Warning Modal */}
      {showActiveWarningModal &&
        createPortal(
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
            <div className="w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 p-6 space-y-5">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-amber-100 text-amber-700 rounded-lg shrink-0 mt-0.5">
                  <MdWarning className="w-6 h-6" />
                </div>
                <div>
                  <h3
                    className="text-base font-bold text-gray-900"
                    style={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    Modifying Active SSO Credentials
                  </h3>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Modifying settings on an <strong>Active (Live)</strong> SSO connection will automatically set the connection status back to <strong>Draft</strong> and <strong>temporarily disable live SSO logins</strong> for your employees until the new credentials are tested and reactivated.
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-normal">
                <strong>What will happen:</strong> Employees attempting to log in via SSO will be blocked until you run <em>Test SSO Connection</em> successfully and set status back to Active.
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowActiveWarningModal(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSaveConnection(e, true)}
                  className="px-4 py-2 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors shadow-xs cursor-pointer"
                >
                  Yes, Save & Reset to Draft
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SSOSettings;
